// RIBBON icon registry — string key → icon component (R0, 2026-07-20).
//
// Rowsets are pure DATA (serializable, node-testable), so control descriptors
// carry icon KEYS, never elements. This registry resolves them at render time.
//
// Reuse, don't fork: keys map to the house toolbarIcons set wherever one
// exists; the handful the ribbon needs beyond it wrap lucide-react locally at
// the same 16px/stroke-2 convention (the Toolbar.jsx AlignJustify precedent —
// toolbarIcons is owned elsewhere and is NOT edited by the ribbon lane).
import React from 'react';
import {
  List, ListOrdered, ListChecks, ListTodo, CaseSensitive, Spline, Waves,
  Circle, Minus, Sun, Layers, Paintbrush, Ruler, Maximize2, Star, Shapes,
  AlignJustify, Frame, Crop, Wand2, Baseline, RectangleHorizontal, Hexagon,
  MoveHorizontal, MoveVertical, Scan, Image as ImageIcon, Ban,
} from 'lucide-react';
import {
  IconTextAlignLeft, IconTextAlignCenter, IconTextAlignRight,
  IconBold, IconItalic, IconUnderline, IconStrikethrough,
  IconOpacity, IconLock, IconDuplicate, IconTrash, IconMore,
  IconEffects, IconPosition, IconStyles, IconOutline, IconGradient,
  IconLayerUp, IconLayerDown, IconLayerTop, IconLayerBottom,
  IconGroup, IconUngroup, IconFlipH, IconFlipV, IconReplace, IconFilters, IconFit,
} from '../../toolbarIcons';

const make = (Cmp) => (props) => <Cmp size={16} strokeWidth={2} aria-hidden="true" {...props} />;

const REGISTRY = {
  // house set (toolbarIcons — reused as-is)
  alignLeft: IconTextAlignLeft,
  alignCenter: IconTextAlignCenter,
  alignRight: IconTextAlignRight,
  bold: IconBold,
  italic: IconItalic,
  underline: IconUnderline,
  strikethrough: IconStrikethrough,
  opacity: IconOpacity,
  lock: IconLock,
  duplicate: IconDuplicate,
  trash: IconTrash,
  more: IconMore,
  effects: IconEffects,
  position: IconPosition,
  styles: IconStyles,
  outline: IconOutline,
  gradient: IconGradient,
  layerUp: IconLayerUp,
  layerDown: IconLayerDown,
  layerTop: IconLayerTop,
  layerBottom: IconLayerBottom,
  group: IconGroup,
  ungroup: IconUngroup,
  flipH: IconFlipH,
  flipV: IconFlipV,
  replace: IconReplace,
  filters: IconFilters,
  fit: IconFit,
  // ribbon-only additions (lucide, house convention)
  alignJustify: make(AlignJustify),
  listBullets: make(List),
  listNumbers: make(ListOrdered),
  listCheckboxes: make(ListChecks),
  listLetters: make(ListTodo),
  listNone: make(Ban),
  caseCycle: make(CaseSensitive),
  curveStraight: make(Minus),
  curveArc: make(Spline),
  curveCircle: make(Circle),
  curveWave: make(Waves),
  posterLight: make(Sun),
  arrange: make(Layers),
  formatPainter: make(Paintbrush),
  ruler: make(Ruler),
  fitBox: make(Maximize2),
  star: make(Star),
  shapes: make(Shapes),
  frame: make(Frame),
  crop: make(Crop),
  wand: make(Wand2),
  baseline: make(Baseline),
  pageWide: make(RectangleHorizontal),
  hexagon: make(Hexagon),
  letterSpacing: make(MoveHorizontal),
  lineHeight: make(MoveVertical),
  margins: make(Scan),
  image: make(ImageIcon),
};

/** Resolve an icon key to a component; unknown keys get a visible "?" stub so
 *  a rowset typo is seen in the lab, not silently blank. */
export function getIcon(key) {
  if (!key) return null;
  return REGISTRY[key] || UnknownIcon(key);
}

const UnknownIcon = (key) => function UnknownRibbonIcon() {
  return <span className="rbn-icon-unknown" title={`Unknown icon key: ${key}`}>?</span>;
};
